/*
  Загрузка состава зарегистрированных - шаг 3 работы с портфелем (30.09.2026).

  Указание владельца: «доделываем шаг 3». До этого дня шаг стоял серым и говорил,
  что финансово-лицевой счёт на вход не принимается. Принимать его бланком и
  правда нельзя - он приходит сканом, - но из бланка системе не хватало ровно
  одного: состава зарегистрированных. Площади грузит вкладка «Помещения»,
  паспорта - «Идентификаторы», а домовая книга приходила только ночным обменом
  с 1С, и у заказчика без обмена таблица жильцов в выписке собиралась из состава
  должников: один человек вместо семьи.

  🔴 Разбор перед записью здесь не формальность. Загрузка ЗАМЕНЯЕТ домовую книгу
  счёта целиком - иначе выбывший жилец остался бы в документе для суда навсегда.
  Поэтому человек сперва видит поимённо, кто окажется в листе, и только потом
  подтверждает.

  02.10.2026, решение владельца (макет А): зона выбора файла одна на оба способа
  - таблицу жильцов и скан выписки. Вид файла виден по имени, и от него зависит,
  что предложено дальше: разбор таблицы с показом будущего листа или отправка
  скана на распознавание (`ResidentsScanWork`). Скан отвергался полем выбора
  файла до этого дня, хотя система читала его с 01.10.2026 - умение было, а в
  шаге, ради которого оно заводилось, его не было.

  06.10.2026, решение владельца (макет Б): и кнопки у обоих видов одни - «чтобы
  было единое поведение и человек не путался». «Посмотреть, что получится» у
  таблицы показывает будущий лист, у скана - отправляет его на распознавание, а
  «посмотреть» для него - постраничная сверка. «Записать состав» скану не
  нужна: его люди записываются после сверки. Отдельной кнопки «Прислать скан»
  больше нет.

  🔴 С 20.10.2026 ТАБЛИЦА идёт путём «файл один раз» (решение владельца
  19.10.2026, `useUploadFlow`): запись - фоновой работой с ходом, конец - блок
  «Готово». Скан этим путём не идёт: файлы загрузки сервер принимает только
  XLSX и CSV, а скан уходит на распознавание прежней дверью вкладки «Файлы».
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { rows as rowsCount } from '../api/format.js'
import { Loading, Notice } from './Common.jsx'
import PickAccounts from './picker/PickAccounts.jsx'
import ResidentsHelp from './ResidentsHelp.jsx'
import ResidentsPlan from './ResidentsPlan.jsx'
import ResidentsScanWork, { isScan } from './ResidentsScanWork.jsx'
import UploadEnd from './upload/UploadEnd.jsx'
import UploadStages from './upload/UploadStages.jsx'
import useUploadFlow from './upload/useUploadFlow.js'

/* Подпись под кнопками - по виду выбранного файла: таблица заменяет состав,
   скан дописывает людей, и человек должен знать это до нажатия. */
const TABLE_NOTE =
  'Таблица заменяет состав счёта целиком: кого нет в новом файле, того не будет и в выписке.'
const SCAN_NOTE =
  '«Посмотреть, что получится» отправит скан на распознавание: Арбитр ЖКХ вычитает жильцов ' +
  'сам, а вы сверите прочитанное с картинкой постранично. Скан не заменяет состав, а ' +
  'дописывает людей: кто уже есть в домовой книге, второй раз не заводится.'

/* Отказ по файлу приходит внутри принятого ответа (договорённость C1,
   01.10.2026): запрос прошёл, а файл не взят. Для человека это ошибка у кнопки -
   с причиной и советом сервера. */
function refusalOf(received) {
  const [refused] = received.refused ?? []
  return refused ? { message: refused.message, remedy: refused.remedy } : null
}

export default function ResidentsUpload({ onLoaded }) {
  const flow = useUploadFlow('residents')
  // Таблица - уже прочитанный файл (20.10.2026); скан - сам файл.
  const file = flow.stored
  const [scanFile, setScanFile] = useState(null)
  const [plan, setPlan] = useState(null)
  const [written, setWritten] = useState(false)
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  /* Отправленный скан убирается из поля вместе с именем (осмотр 02.10.2026):
     своё значение родное поле выбора файла держит само, и без пересоздания
     человек видел имя уже отправленного файла рядом с кнопками для таблицы.
     С 20.10.2026 так же пересоздаёт поле «Загрузить другой файл». */
  const [round, setRound] = useState(0)
  // Номера сканов, присланных отсюда, - блоку сканов: он перечитает список.
  const [sent, setSent] = useState([])
  // Счета из окна «Какие счета загрузить» (16.10.2026); пусто - весь файл.
  const [only, setOnly] = useState(null)

  function choose(chosen) {
    const scanned = isScan(chosen)
    setScanFile(scanned ? chosen : null)
    setPlan(null)
    setOnly(null)
    setWritten(false)
    setError(null)
    flow.choose(scanned ? null : (chosen ?? null))
  }

  function forget() {
    choose(null)
    setRound((number) => number + 1)
  }

  // Возвращает «получилось»: кнопке выбора счетов нужно знать, открывать ли окно.
  async function act(action, then) {
    setBusy(true)
    setError(null)
    try {
      then(await action())
      return true
    } catch (failure) {
      setError(failure)
      return false
    } finally {
      setBusy(false)
    }
  }

  const scan = Boolean(scanFile)

  const send = () =>
    act(
      () => api.sendUploads([scanFile]),
      (received) => {
        const refusal = refusalOf(received)
        if (refusal) {
          setError(refusal)
          return
        }
        setSent(received.accepted.map((accepted) => accepted.id))
        forget()
      },
    )

  const look = () =>
    scan
      ? send()
      : act(
          () =>
            flow.check(
              () => api.previewResidents(file, null, only),
              (seen) => `в файле ${rowsCount(seen.total)}, жильцов к записи: ${seen.written}`,
            ),
          setPlan,
        )
  // Другой выбор счетов - другой предпросмотр: человек подтверждает то, что запишется.
  const choosePart = (numbers) => {
    setOnly(numbers)
    act(() => api.previewResidents(file, null, numbers), setPlan)
  }

  // Запись - фоновой работой (20.10.2026); ответ тот же: обработчик сервера прежний.
  const load = () =>
    act(
      () =>
        flow.write('residents', {
          column_overrides: null,
          only_accounts: only ? JSON.stringify(only) : null,
        }),
      (done) => {
        setPlan(done)
        setWritten(true)
        // Чек-лист готовности считает счета с домовой книгой, и лента шагов
        // красит по нему же: после записи оба обязаны пересчитаться.
        // Номер загрузки нужен журналу шага - он ждёт ИМЕННО эту строку.
        onLoaded(done.import_run_id)
      },
    )

  return (
    <>
      <section className="section card">
        <div className="pickfile">
          <label className="pickfile__control">
            <span className="pickfile__label">Файл домовой книги</span>
            <span className="pickfile__note">
              таблица со строкой на жильца или скан выписки: CSV, XLSX, PDF, JPG, PNG
            </span>
            <input
              key={`${round}-${flow.attempt}`}
              className="field__file pickfile__input"
              type="file"
              accept=".csv,.xlsx,.pdf,.jpg,.jpeg,.png"
              disabled={busy || flow.busy}
              onChange={(event) => choose(event.target.files?.[0])}
            />
          </label>
        </div>
        {/* У скана путь пуст, и `UploadStages` не рисует ничего. */}
        <UploadStages flow={flow} />

        <div className="row">
          <button
            type="button"
            className="button"
            disabled={busy || !(scan || file) || written}
            onClick={look}
          >
            Посмотреть, что получится
          </button>
          {/* У скана номеров счетов до прочтения нет - выбирать не из чего. */}
          {scan ? null : (
            <PickAccounts
              choices={plan?.choices ?? null}
              only={only}
              onChange={choosePart}
              onNeed={look}
              disabled={busy || !file || written}
              note={file ? `${file.name} · счетов в файле: ${plan?.choices.length ?? '…'}` : null}
              amountTitle={null}
            />
          )}
          <button
            type="button"
            className="button"
            disabled={busy || !file || scan || !plan || written}
            onClick={load}
          >
            Записать состав
          </button>
        </div>
        <p className="muted">{scan ? SCAN_NOTE : TABLE_NOTE}</p>

        {/* Ход таблицы - в «Ходе загрузки»; здесь ждёт только отправка скана. */}
        {busy && scan ? <Loading what="Отправляем скан" /> : null}
        <Notice error={flow.error ?? error} />
        {/* Конец - блок «Готово» (20.10.2026); поимённый состав - под ним. */}
        {written ? (
          <UploadEnd
            title="Готово: состав зарегистрированных записан"
            facts={[
              ['Записано жильцов', plan.written],
              ['Счетов', plan.accounts],
              ['Строк, оставшихся вам', plan.issues.length],
            ]}
            seconds={flow.written?.seconds}
            onAgain={forget}
          />
        ) : null}
        {plan ? <ResidentsPlan plan={plan} /> : null}
        {plan?.left_out ? (
          <p className="muted">
            Не выбраны вами - их домовые книги не тронуты: {rowsCount(plan.left_out)}.
          </p>
        ) : null}
      </section>

      <ResidentsScanWork sent={sent} />

      <ResidentsHelp />
    </>
  )
}
